import React from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

export default function CartPanel() {
  const navigate = useNavigate();
  const { items, removeFromCart, updateQty, total, count, isOpen, setIsOpen } = useCart();
  const { user, setLoginModalOpen, setLoginRedirect } = useAuth();

  const handleCheckout = () => {
    setIsOpen(false);
    if (!user) {
      setLoginRedirect('/checkout');
      setLoginModalOpen(true);
      return;
    }
    navigate('/checkout');
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white z-50 flex flex-col shadow-2xl transition-transform duration-500 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} />
            <span className="font-display text-xl tracking-wide">Your Cart</span>
            {count > 0 && <span className="text-xs text-gray-400">({count} items)</span>}
          </div>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-black transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={40} className="text-gray-200 mb-4" />
              <p className="text-gray-400 text-sm tracking-wide">Your cart is empty</p>
              <button
                onClick={() => setIsOpen(false)}
                className="mt-6 text-xs tracking-widest uppercase border-b border-black pb-0.5 hover:text-gray-500 transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {items.map(item => (
                <div key={item.id} className="flex gap-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight truncate">{item.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.size}</p>
                    <p className="text-sm font-medium mt-1">₹{(item.price * item.qty).toLocaleString()}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-gray-200 rounded">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="px-2 py-1 text-gray-400 hover:text-black transition-colors"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm font-medium">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="px-2 py-1 text-gray-400 hover:text-black transition-colors"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-gray-300 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-6 border-t border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-gray-500">Subtotal</span>
              <span className="font-medium text-lg">₹{total.toLocaleString()}</span>
            </div>
            {total < 499 && (
              <p className="text-xs text-[#cc7c66] mb-4 text-center">
                Add ₹{(499 - total).toLocaleString()} more for free shipping
              </p>
            )}
            <button
              onClick={handleCheckout}
              className="w-full bg-black text-white py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors"
            >
              Proceed to Checkout
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="w-full mt-3 text-xs tracking-widest uppercase text-gray-400 hover:text-black transition-colors py-2"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
